import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GoldButton, GhostButton } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Star, Euro, ShieldOff, ShieldCheck, ScanLine, Upload } from "lucide-react";
import { BlockModal } from "@/pages/admin/BlockedDashboard";
import OCRUploadModal from "@/components/OCRUploadModal";

const LANGS = ["PT", "EN", "ES", "FR", "DE", "IT", "ZH"];

export default function DriverDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [savingSection, setSavingSection] = useState("");
  const [blockOpen, setBlockOpen] = useState(false);

  const load = () => api.get(`/admin/drivers/${id}`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  if (!d) return <div className="p-10 text-slate-500">Loading…</div>;

  const save = async (payload, label) => {
    setSavingSection(label);
    try {
      await api.patch(`/admin/drivers/${id}`, payload);
      toast.success(`${label} saved`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingSection(""); }
  };

  const doApproval = async (action, reason) => {
    try {
      await api.post(`/admin/drivers/${id}/approval`, { action, reason });
      toast.success(`Driver ${action}d`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10 max-w-[1400px]">
      <Link to="/admin/drivers" className="inline-flex items-center gap-2 text-xs uppercase text-slate-500 hover:text-[#f59e0b] mb-6"><ArrowLeft className="w-3.5 h-3.5" /> All drivers</Link>

      <PageHeader eyebrow={`Driver · ${d.id.slice(0, 8)}`} title={d.name} description={d.bio_en || d.bio_pt || "Driver profile & configuration"}>
        <StatBadge status={d.status} />
        {d.status === "pending" && (
          <>
            <GoldButton onClick={() => doApproval("approve")} data-testid="approve-driver"><Check className="w-4 h-4" /> Approve</GoldButton>
            <GhostButton onClick={() => doApproval("reject", "Documents insufficient")} data-testid="reject-driver"><X className="w-4 h-4" /> Reject</GhostButton>
          </>
        )}
        {d.status === "approved" && (
          <GhostButton onClick={() => setBlockOpen(true)} data-testid="block-driver-btn" className="!border-red-300 !text-red-700 hover:!bg-red-50">
            <ShieldOff className="w-4 h-4" /> Bloquear
          </GhostButton>
        )}
        {d.status === "suspended" && (
          <>
            <GoldButton
              onClick={async () => {
                try {
                  await api.post(`/admin/drivers/${d.id}/unblock`);
                  toast.success("Motorista desbloqueado");
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              data-testid="unblock-driver-btn"
            >
              <ShieldCheck className="w-4 h-4" /> Desbloquear
            </GoldButton>
            {d.block_reason && (
              <div className="text-xs text-red-700 max-w-sm">
                <span className="font-semibold uppercase">Motivo:</span> {d.block_reason}
                {d.blocked_auto && <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-800 text-[9px] uppercase font-bold">Auto</span>}
              </div>
            )}
          </>
        )}
      </PageHeader>
      <BlockModal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        entityLabel={`motorista ${d.name}`}
        onConfirm={async (reason) => {
          await api.post(`/admin/drivers/${d.id}/block`, { reason });
          toast.success("Motorista bloqueado");
          load();
        }}
      />

      {/* Quick facts */}
      <div className="grid grid-cols-4 gap-5 mb-8">
        <FactCard label="Rating" value={<span className="flex items-center gap-1"><Star className="w-4 h-4 text-[#f59e0b] fill-[#f59e0b]" />{d.rating_avg?.toFixed(1) || "—"}</span>} sub={`${d.trips_count} trips`} />
        <FactCard label="Earnings (lifetime)" value={`€${(d.total_earnings || 0).toFixed(2)}`} icon={Euro} />
        <FactCard label="Zones" value={(d.zones_covered || []).length} sub={(d.zones_covered || []).join(" · ")} />
        <FactCard label="Veículo primário" value={
          (() => {
            const pv = (d.fleet_vehicles || []).find((x) => x.id === d.primary_vehicle_id) || (d.vehicles || []).find((x) => x.id === d.primary_vehicle_id);
            return pv ? `${pv.brand} ${pv.model}` : "—";
          })()
        } sub={
          (() => {
            const pv = (d.fleet_vehicles || []).find((x) => x.id === d.primary_vehicle_id) || (d.vehicles || []).find((x) => x.id === d.primary_vehicle_id);
            const subs = (d.substitute_vehicle_ids || []).length;
            return pv ? `${pv.plate}${subs ? ` · +${subs} sub.` : ""}` : "Não atribuído";
          })()
        } />
      </div>

      <Tabs defaultValue="personal">
        <TabsList className="bg-transparent border border-slate-200 rounded-none h-auto p-0 flex flex-wrap">
          {["personal", "vehicle", "docs", "pricing", "schedule", "history", "reviews"].map((k) => (
            <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
              className="px-5 py-2.5 text-xs uppercase tracking-wide rounded-none data-[state=active]:bg-[#f59e0b] data-[state=active]:text-[#0a0f1e] text-slate-500">
              {k}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Personal */}
        <TabsContent value="personal" className="mt-8">
          <PersonalTab d={d} save={save} saving={savingSection === "Personal"} />
        </TabsContent>

        {/* Vehicle assignment (Iter 23) */}
        <TabsContent value="vehicle" className="mt-8">
          <VehicleTab d={d} save={save} saving={savingSection === "Vehicle"} />
        </TabsContent>

        {/* Docs */}
        <TabsContent value="docs" className="mt-8">
          <DocsTab d={d} save={save} saving={savingSection === "Documents"} />
        </TabsContent>

        {/* Pricing */}
        <TabsContent value="pricing" className="mt-8">
          <PricingTab d={d} save={save} saving={savingSection === "Pricing"} />
        </TabsContent>

        {/* Schedule */}
        <TabsContent value="schedule" className="mt-8">
          <ScheduleTab d={d} save={save} saving={savingSection === "Schedule"} />
        </TabsContent>

        {/* Iter 23 Sub-Fase A — Historial de serviços e ganhos */}
        <TabsContent value="history" className="mt-8">
          <DriverHistoryTab d={d} />
        </TabsContent>

        {/* Reviews */}
        <TabsContent value="reviews" className="mt-8">
          <div className="border border-slate-200 bg-white p-6">
            {d.reviews?.length === 0 && <div className="text-slate-500 text-sm">No reviews yet.</div>}
            {d.reviews?.map((r) => (
              <div key={r.id} className="border-b border-slate-200 py-4">
                <div className="flex items-center gap-2 mb-1">
                  {Array.from({ length: r.stars }).map((_, i) => <Star key={i} className="w-3 h-3 text-[#f59e0b] fill-[#f59e0b]" />)}
                </div>
                <div className="text-sm text-slate-700">{r.comment}</div>
                <div className="text-xs text-slate-500 mt-1 font-mono">{new Date(r.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FactCard({ label, value, sub, icon: Icon }) {
  return (
    <div className="border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="label-caps">{label}</span>
        {Icon && <Icon className="w-3.5 h-3.5 text-[#f59e0b]" />}
      </div>
      <div className="font-display text-2xl text-[#0a0f1e]">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 truncate">{sub}</div>}
    </div>
  );
}

function VehicleTab({ d, save, saving }) {
  const [fleets, setFleets] = useState([]);
  const [f, setF] = useState({
    fleet_id: d.fleet_id || "",
    primary_vehicle_id: d.primary_vehicle_id || "",
    substitute_vehicle_ids: d.substitute_vehicle_ids || [],
  });
  useEffect(() => {
    api.get("/admin/fleets").then((r) => setFleets(r.data || [])).catch(() => {});
  }, []);
  const fleetVehicles = d.fleet_vehicles || [];
  const toggleSub = (vid) => {
    if (vid === f.primary_vehicle_id) {
      toast.error("O veículo primário não pode estar também nos substitutos");
      return;
    }
    setF({
      ...f,
      substitute_vehicle_ids: f.substitute_vehicle_ids.includes(vid)
        ? f.substitute_vehicle_ids.filter((x) => x !== vid)
        : [...f.substitute_vehicle_ids, vid],
    });
  };
  const currentFleet = fleets.find((x) => x.id === f.fleet_id);
  return (
    <div className="space-y-6">
      <div className="border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
        <strong>Regra:</strong> cada motorista tem um <em>veículo primário</em> obrigatório. Pode substituir noutros veículos da mesma frota. Vários motoristas podem partilhar o mesmo veículo (turnos).
      </div>

      <Section title="Frota associada" full>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label-caps block mb-2">Frota</label>
            <select
              data-testid="driver-fleet-select"
              value={f.fleet_id}
              onChange={(e) => setF({ ...f, fleet_id: e.target.value, primary_vehicle_id: "", substitute_vehicle_ids: [] })}
              className="input-dt"
            >
              <option value="">— Sem frota (independente) —</option>
              {fleets.map((fl) => (
                <option key={fl.id} value={fl.id}>{fl.company_name} · {fl.nif}</option>
              ))}
            </select>
            {currentFleet && (
              <Link
                to={`/admin/fleets/${currentFleet.id}`}
                data-testid="driver-fleet-link"
                className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold"
              >
                Ver ficha da frota →
              </Link>
            )}
          </div>
          <div className="flex items-end text-xs text-slate-600">
            {currentFleet ? (
              <div>
                <div className="label-caps mb-1">Contacto</div>
                <div>{currentFleet.email || currentFleet.phone || "—"}</div>
              </div>
            ) : (
              <div className="text-[10px] text-slate-500">Motorista independente — pode operar sem frota, com veículo próprio.</div>
            )}
          </div>
        </div>
      </Section>

      <Section title="Veículo primário (obrigatório)" full>
        {fleetVehicles.length === 0 && (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 p-6 text-center">
            Sem veículos disponíveis {f.fleet_id ? "nesta frota" : "sem frota"}. Adicione um veículo primeiro.
          </div>
        )}
        {f.primary_vehicle_id && (() => {
          const pv = fleetVehicles.find((v) => v.id === f.primary_vehicle_id);
          if (!pv) return null;
          return (
            <div className="mb-4 border border-[#f59e0b] bg-amber-50 p-4 flex items-center justify-between" data-testid="primary-vehicle-summary">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-bold">Veículo atribuído</div>
                <div className="mt-1 font-display text-lg text-[#0a0f1e]">{pv.brand} {pv.model} <span className="font-mono text-sm text-[#f59e0b] ml-2">{pv.plate}</span></div>
                <div className="text-xs text-slate-600 mt-1">{pv.type} · {pv.capacity_passengers}p · {pv.fuel_type}
                  {(pv.assigned_categories || []).length > 0 && (
                    <span className="ml-2">
                      · Categorias: {pv.assigned_categories.map((c) => (
                        <span key={c} className="ml-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#f59e0b]/10 border border-[#f59e0b]/40 text-[#f59e0b] font-bold">{c}</span>
                      ))}
                    </span>
                  )}
                </div>
              </div>
              <Link
                to={`/admin/vehicles?vehicle_id=${pv.id}`}
                data-testid="driver-vehicle-link"
                className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:underline font-bold whitespace-nowrap"
              >
                Ver ficha →
              </Link>
            </div>
          );
        })()}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fleetVehicles.map((v) => (
            <button
              key={v.id}
              type="button"
              data-testid={`primary-vehicle-${v.id}`}
              onClick={() => setF({ ...f, primary_vehicle_id: v.id, substitute_vehicle_ids: f.substitute_vehicle_ids.filter((x) => x !== v.id) })}
              className={`text-left border p-4 transition-colors ${f.primary_vehicle_id === v.id ? "border-[#f59e0b] bg-amber-50" : "border-slate-200 hover:border-slate-300"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-semibold text-[#0a0f1e]">{v.brand} {v.model}</div>
                <div className="text-[10px] font-mono uppercase text-[#f59e0b]">{v.plate}</div>
              </div>
              <div className="text-xs text-slate-600">{v.type} · {v.capacity_passengers}p · {v.fuel_type}</div>
              {f.primary_vehicle_id === v.id && <div className="mt-2 text-[10px] uppercase font-bold text-[#f59e0b]">✓ Primário</div>}
            </button>
          ))}
        </div>
      </Section>

      <Section title="Veículos que pode substituir (opcional)" full>
        <div className="text-xs text-slate-600 mb-3">Selecione outros veículos da frota onde este motorista pode conduzir em substituição.</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {fleetVehicles
            .filter((v) => v.id !== f.primary_vehicle_id)
            .map((v) => (
              <button
                key={v.id}
                type="button"
                data-testid={`sub-vehicle-${v.id}`}
                onClick={() => toggleSub(v.id)}
                className={`text-left border p-3 transition-colors ${f.substitute_vehicle_ids.includes(v.id) ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-[#0a0f1e]">{v.brand} {v.model}</div>
                  <div className="text-[10px] font-mono uppercase text-slate-600">{v.plate}</div>
                </div>
                {f.substitute_vehicle_ids.includes(v.id) && <div className="mt-1 text-[10px] uppercase font-bold text-emerald-700">✓ Substituto</div>}
              </button>
            ))}
        </div>
      </Section>

      <div className="flex justify-end">
        <GoldButton
          disabled={saving}
          data-testid="save-vehicle-assignment"
          onClick={() => {
            if (!f.primary_vehicle_id) {
              toast.error("Selecione um veículo primário");
              return;
            }
            save(
              {
                fleet_id: f.fleet_id || null,
                primary_vehicle_id: f.primary_vehicle_id,
                substitute_vehicle_ids: f.substitute_vehicle_ids,
              },
              "Vehicle"
            );
          }}
        >
          {saving ? "A guardar…" : "Guardar atribuição"}
        </GoldButton>
      </div>
    </div>
  );
}

function PersonalTab({ d, save, saving }) {
  const [f, setF] = useState({
    name: d.name, phone: d.phone, nif: d.nif, photo_url: d.photo_url || "",
    bio_pt: d.bio_pt || "", bio_en: d.bio_en || "",
    languages: d.languages || [], zones_covered: (d.zones_covered || []).join(", "),
    base_location: d.base_location, max_distance_km: d.max_distance_km,
  });
  const toggleLang = (l) => setF({ ...f, languages: f.languages.includes(l) ? f.languages.filter(x => x !== l) : [...f.languages, l] });
  return (
    <div className="grid grid-cols-2 gap-6">
      <Section title="Identity">
        <Field label="Full name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="input-dt" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="input-dt" /></Field>
        <Field label="NIF"><Input value={f.nif} onChange={(e) => setF({ ...f, nif: e.target.value })} className="input-dt" /></Field>
        <Field label="Photo URL"><Input value={f.photo_url} onChange={(e) => setF({ ...f, photo_url: e.target.value })} className="input-dt" /></Field>
      </Section>
      <Section title="Zones & Languages">
        <Field label="Base location"><Input value={f.base_location} onChange={(e) => setF({ ...f, base_location: e.target.value })} className="input-dt" /></Field>
        <Field label="Max distance (km)"><Input type="number" value={f.max_distance_km} onChange={(e) => setF({ ...f, max_distance_km: Number(e.target.value) })} className="input-dt" /></Field>
        <Field label="Zones (comma separated)"><Input value={f.zones_covered} onChange={(e) => setF({ ...f, zones_covered: e.target.value })} className="input-dt" /></Field>
        <Field label="Languages">
          <div className="flex flex-wrap gap-2">
            {LANGS.map((l) => (
              <button key={l} type="button" onClick={() => toggleLang(l)} data-testid={`lang-${l}`}
                className={`px-3 py-1.5 text-xs border ${f.languages.includes(l) ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500"}`}>
                {l}
              </button>
            ))}
          </div>
        </Field>
      </Section>
      <Section title="Bio (PT)" full>
        <Textarea value={f.bio_pt} onChange={(e) => setF({ ...f, bio_pt: e.target.value })} rows={3} className="input-dt" />
      </Section>
      <Section title="Bio (EN)" full>
        <Textarea value={f.bio_en} onChange={(e) => setF({ ...f, bio_en: e.target.value })} rows={3} className="input-dt" />
      </Section>
      <div className="col-span-2 flex justify-end">
        <GoldButton disabled={saving} data-testid="save-personal" onClick={() => save({
          name: f.name, phone: f.phone, nif: f.nif, photo_url: f.photo_url,
          bio_pt: f.bio_pt, bio_en: f.bio_en, languages: f.languages,
          zones_covered: f.zones_covered.split(",").map(s => s.trim()).filter(Boolean),
          base_location: f.base_location, max_distance_km: f.max_distance_km,
        }, "Personal")}>{saving ? "Saving…" : "Save personal"}</GoldButton>
      </div>
    </div>
  );
}

const REQUIRED_DRIVER_DOCS = [
  { key: "id_front", label: "ID (frente) — CC / Passaporte / Autorização", accept: ["id_front", "passport", "residence_permit"] },
  { key: "id_back", label: "ID (verso)", accept: ["id_back"] },
  { key: "driving_license_front", label: "Carta de Condução (frente)", accept: ["driving_license_front"] },
  { key: "driving_license_back", label: "Carta de Condução (verso)", accept: ["driving_license_back"] },
  { key: "criminal_record", label: "Registo Criminal (com validade)", accept: ["criminal_record"] },
];

function DocsTab({ d, save, saving }) {
  const [docs, setDocs] = useState(d.documents || []);
  const [ocrOpen, setOcrOpen] = useState(null); // { key, docType, label }
  const update = (i, key, val) => setDocs(docs.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const add = () => setDocs([...docs, { document_type: "driving_license", number: "", expiry_date: "" }]);
  const remove = (i) => setDocs(docs.filter((_, idx) => idx !== i));

  const applyOcrResult = (target, result) => {
    const f = result.fields || {};
    // Map fields to a doc entry
    const number = f.id_number || f.license_number || f.passport_number || f.permit_number || f.certificate_number || "";
    const entry = {
      document_type: target.key,
      number,
      full_name: f.full_name || null,
      birth_date: f.birth_date || null,
      issue_date: f.issue_date || null,
      expiry_date: f.expiry_date || null,
      file_url: result.file_url || null,
      ocr_confidence: result.confidence || 0,
      ocr_raw_text: result.raw_text || "",
      extracted_fields: f,
    };
    // Replace existing with same doc_type or append
    const idx = docs.findIndex((x) => x.document_type === target.key);
    if (idx >= 0) {
      const next = [...docs]; next[idx] = { ...next[idx], ...entry };
      setDocs(next);
    } else {
      setDocs([...docs, entry]);
    }
    toast.success(`${target.label} extraído com sucesso`);
  };

  const findDoc = (key) => docs.find((x) => x.document_type === key);

  return (
    <div className="space-y-6">
      {/* OCR upload cards */}
      <div className="border border-[#f59e0b]/30 bg-amber-50/40 p-5">
        <div className="flex items-center gap-2 mb-3">
          <ScanLine className="w-4 h-4 text-[#f59e0b]" />
          <div className="label-caps">Documentos obrigatórios · OCR automático (Gemini Vision)</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {REQUIRED_DRIVER_DOCS.map((doc) => {
            const existing = findDoc(doc.key);
            return (
              <div key={doc.key} className={`border p-3 bg-white ${existing ? "border-emerald-300" : "border-slate-200"}`} data-testid={`doc-card-${doc.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-[#0a0f1e]">{doc.label}</div>
                    {existing ? (
                      <div className="text-[10px] font-mono text-slate-600 mt-1">
                        Nº <span className="text-[#0a0f1e] font-bold">{existing.number || "—"}</span>
                        {existing.expiry_date && <span className="ml-2">Val: <span className="text-[#0a0f1e] font-bold">{existing.expiry_date}</span></span>}
                        {existing.ocr_confidence != null && <span className="ml-2 text-emerald-700">✓ OCR {(existing.ocr_confidence * 100).toFixed(0)}%</span>}
                      </div>
                    ) : (
                      <div className="text-[10px] text-slate-500 mt-1">Ainda não carregado</div>
                    )}
                  </div>
                  <button
                    onClick={() => setOcrOpen({ key: doc.key, docType: doc.accept[0], label: doc.label })}
                    data-testid={`ocr-btn-${doc.key}`}
                    className="text-[10px] uppercase tracking-widest font-bold border border-[#f59e0b] text-[#f59e0b] hover:bg-amber-50 px-3 py-2"
                  >
                    <Upload className="w-3 h-3 inline mr-1" /> {existing ? "Substituir" : "Carregar"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legacy table */}
      <div className="border border-slate-200 bg-white p-6">
        <div className="label-caps mb-3">Todos os documentos (edição manual)</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps text-left border-b border-slate-200">
              <th className="p-2">Type</th><th className="p-2">Number</th><th className="p-2">Expiry</th><th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {docs.map((doc, i) => (
              <tr key={i} className="border-b border-slate-200">
                <td className="p-2"><Input value={doc.document_type} onChange={(e) => update(i, "document_type", e.target.value)} className="input-dt" data-testid={`doc-type-${i}`} /></td>
                <td className="p-2"><Input value={doc.number || ""} onChange={(e) => update(i, "number", e.target.value)} className="input-dt" /></td>
                <td className="p-2"><Input type="date" value={doc.expiry_date || ""} onChange={(e) => update(i, "expiry_date", e.target.value)} className="input-dt" /></td>
                <td className="p-2"><button onClick={() => remove(i)} className="text-slate-500 hover:text-red-400"><X className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between mt-4">
          <GhostButton onClick={add} data-testid="add-doc">+ Add document</GhostButton>
          <GoldButton disabled={saving} onClick={() => save({ documents: docs }, "Documents")} data-testid="save-docs">Save documents</GoldButton>
        </div>
      </div>

      {ocrOpen && (
        <OCRUploadModal
          documentType={ocrOpen.docType}
          label={ocrOpen.label}
          onCancel={() => setOcrOpen(null)}
          onConfirm={(res) => { applyOcrResult(ocrOpen, res); setOcrOpen(null); }}
        />
      )}
    </div>
  );
}

function PricingTab({ d, save, saving }) {
  const [f, setF] = useState({
    pricing_km: d.pricing_km, pricing_hour: d.pricing_hour, pricing_method: d.pricing_method,
    night_supplement_pct: d.night_supplement_pct, weekend_supplement_pct: d.weekend_supplement_pct,
    fixed_routes: d.fixed_routes || [],
  });
  const updRoute = (i, key, val) => setF({ ...f, fixed_routes: f.fixed_routes.map((r, idx) => idx === i ? { ...r, [key]: key === "price" ? Number(val) : val } : r) });
  const addRoute = () => setF({ ...f, fixed_routes: [...f.fixed_routes, { origin: "", destination: "", price: 0 }] });
  const rmRoute = (i) => setF({ ...f, fixed_routes: f.fixed_routes.filter((_, idx) => idx !== i) });
  return (
    <div className="grid grid-cols-2 gap-6">
      <Section title="Rates">
        <Field label="Price per km (€)"><Input type="number" step="0.01" value={f.pricing_km} onChange={(e) => setF({ ...f, pricing_km: Number(e.target.value) })} className="input-dt" data-testid="price-km" /></Field>
        <Field label="Price per hour (€)"><Input type="number" step="0.01" value={f.pricing_hour} onChange={(e) => setF({ ...f, pricing_hour: Number(e.target.value) })} className="input-dt" data-testid="price-hour" /></Field>
        <Field label="Pricing method">
          <div className="flex gap-2">
            {["higher", "lower"].map((m) => (
              <button key={m} type="button" onClick={() => setF({ ...f, pricing_method: m })}
                className={`flex-1 py-2 text-xs uppercase border ${f.pricing_method === m ? "border-[#f59e0b] text-[#f59e0b]" : "border-slate-200 text-slate-500"}`}>{m} value</button>
            ))}
          </div>
        </Field>
        <Field label="Night supplement % (22h–06h)"><Input type="number" value={f.night_supplement_pct} onChange={(e) => setF({ ...f, night_supplement_pct: Number(e.target.value) })} className="input-dt" /></Field>
        <Field label="Weekend supplement %"><Input type="number" value={f.weekend_supplement_pct} onChange={(e) => setF({ ...f, weekend_supplement_pct: Number(e.target.value) })} className="input-dt" /></Field>
      </Section>
      <Section title="Fixed routes">
        <div className="space-y-2">
          {f.fixed_routes.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_100px_auto] gap-2">
              <Input placeholder="Origin" value={r.origin} onChange={(e) => updRoute(i, "origin", e.target.value)} className="input-dt" />
              <Input placeholder="Destination" value={r.destination} onChange={(e) => updRoute(i, "destination", e.target.value)} className="input-dt" />
              <Input type="number" placeholder="€" value={r.price} onChange={(e) => updRoute(i, "price", e.target.value)} className="input-dt" />
              <button onClick={() => rmRoute(i)} className="text-slate-500 hover:text-red-400 px-2"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <GhostButton onClick={addRoute} data-testid="add-route">+ Add fixed route</GhostButton>
        </div>
      </Section>
      <div className="col-span-2 flex justify-end">
        <GoldButton disabled={saving} onClick={() => save(f, "Pricing")} data-testid="save-pricing">Save pricing</GoldButton>
      </div>
    </div>
  );
}

function ScheduleTab({ d, save, saving }) {
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [wh, setWh] = useState(d.working_hours || DAYS.map((_, i) => ({ day: i, enabled: true, start: "08:00", end: "22:00" })));
  const [blocked, setBlocked] = useState((d.blocked_dates || []).join(", "));
  const upd = (i, key, val) => setWh(wh.map((w, idx) => idx === i ? { ...w, [key]: val } : w));
  return (
    <div className="border border-slate-200 bg-white p-6 space-y-4">
      {DAYS.map((day, i) => {
        const w = wh[i] || { day: i, enabled: false, start: "08:00", end: "22:00" };
        return (
          <div key={day} className="grid grid-cols-[80px_60px_1fr_1fr] items-center gap-4">
            <div className="text-sm text-[#0a0f1e] uppercase tracking-wide">{day}</div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={w.enabled} onChange={(e) => upd(i, "enabled", e.target.checked)} className="accent-[#f59e0b]" data-testid={`day-${day}`} />
              <span className="text-xs text-slate-500">On</span>
            </label>
            <Input type="time" value={w.start} onChange={(e) => upd(i, "start", e.target.value)} className="input-dt" disabled={!w.enabled} />
            <Input type="time" value={w.end} onChange={(e) => upd(i, "end", e.target.value)} className="input-dt" disabled={!w.enabled} />
          </div>
        );
      })}
      <div>
        <label className="label-caps mb-2 block">Blocked dates (comma-separated YYYY-MM-DD)</label>
        <Input value={blocked} onChange={(e) => setBlocked(e.target.value)} className="input-dt" placeholder="2026-03-15, 2026-04-01" />
      </div>
      <div className="flex justify-end">
        <GoldButton disabled={saving} data-testid="save-schedule"
          onClick={() => save({ working_hours: wh, blocked_dates: blocked.split(",").map(s => s.trim()).filter(Boolean) }, "Schedule")}>
          Save schedule
        </GoldButton>
      </div>
    </div>
  );
}

function Section({ title, children, full }) {
  return (
    <div className={`border border-slate-200 bg-white p-6 ${full ? "col-span-2" : ""}`}>
      <div className="label-caps mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="label-caps block mb-2">{label}</label>
      {children}
    </div>
  );
}

function DriverHistoryTab({ d }) {
  const h = d.history || {};
  const monthly = h.monthly || [];
  const trips = h.trips_all || [];
  const maxEarn = Math.max(1, ...monthly.map((m) => m.earnings));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-[#f59e0b] bg-amber-50 p-5" data-testid="history-total-earnings">
          <div className="label-caps mb-1">Ganhos totais</div>
          <div className="font-display text-3xl text-[#f59e0b]">€{Number(h.total_earnings || 0).toFixed(2)}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-1">Payouts acumulados (completed + in progress)</div>
        </div>
        <div className="border border-slate-200 bg-white p-5">
          <div className="label-caps mb-1">Viagens totais</div>
          <div className="font-display text-3xl text-[#0a0f1e]">{h.total_trips || 0}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-1">Todas as reservas atribuídas</div>
        </div>
        <div className="border border-slate-200 bg-white p-5">
          <div className="label-caps mb-1">Média por viagem</div>
          <div className="font-display text-3xl text-[#0a0f1e]">€{h.total_trips ? (h.total_earnings / h.total_trips).toFixed(2) : "0.00"}</div>
          <div className="text-[10px] text-slate-500 font-mono mt-1">Payout médio</div>
        </div>
      </div>

      {monthly.length > 0 && (
        <div className="border border-slate-200 bg-white p-6">
          <div className="label-caps mb-4">Ganhos por mês</div>
          <div className="space-y-2" data-testid="monthly-chart">
            {monthly.slice(0, 12).map((m) => (
              <div key={m.month} className="flex items-center gap-3">
                <div className="w-16 text-[10px] font-mono uppercase text-slate-600">{m.month}</div>
                <div className="flex-1 h-6 bg-slate-100 relative">
                  <div className="h-full bg-[#f59e0b]" style={{ width: `${Math.max(2, (m.earnings / maxEarn) * 100)}%` }} />
                </div>
                <div className="w-24 text-right font-mono text-xs text-[#0a0f1e]">€{m.earnings.toFixed(2)}</div>
                <div className="w-16 text-right text-[10px] text-slate-500 font-mono">{m.trips} viagens</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-slate-200 bg-white">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="label-caps">Histórico completo de serviços ({trips.length})</div>
        </div>
        {trips.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Sem serviços registados.</div>
        ) : (
          <div className="max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="trips-table">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200 label-caps text-left">
                  <th className="p-3">Ref</th>
                  <th className="p-3">Data</th>
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Rota</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3 text-right">Payout</th>
                </tr>
              </thead>
              <tbody>
                {trips.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 font-mono text-[#f59e0b] text-xs">{t.reference}</td>
                    <td className="p-3 text-xs text-slate-700">{t.service_datetime?.slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 text-xs uppercase text-slate-500">{t.type}</td>
                    <td className="p-3 text-xs text-slate-600 max-w-[300px]">
                      <div className="truncate">{t.pickup_address}</div>
                      <div className="truncate">→ {t.destination_address}</div>
                    </td>
                    <td className="p-3 text-xs uppercase text-slate-700">{t.status}</td>
                    <td className="p-3 text-right font-mono text-[#0a0f1e]">€{Number(t.driver_payout_eur || t.driver_earning || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
